import React, { useEffect, useRef, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { useLocation, useSearchParams } from 'react-router-dom';
import { candidateProfileSearchConversationSx } from 'modules/candidate-profile/styles';
import { SearchCandidateProfileMessageSender } from 'generated-graphql/types';
import { SearchCandidateVectorQueryMessage } from 'modules/candidate-profile/types/SearchCandidateVectorQueryMessage';
import { AnalysingLoadingGif } from 'common/animations/AnalysingLoadingGif';
import { CandidateProfileUserMessage } from 'modules/candidate-profile/components/CandidateProfileUserMessage';
import { CandidateProfileAgentMessage } from 'modules/candidate-profile/components/CandidateProfileAgentMessage';
import { CandidateProfileFilterNotice } from 'modules/candidate-profile/components/CandidateProfileFilterNotice';
import {
  CandidateFilterState,
  CandidateRangeFilter
} from 'modules/candidate-profile/types/CandidateSearchFilter';
import { CandidateProfileLinkData } from 'modules/candidate-profile/utils/parseMessageWithCandidatesAndChips';
import { CandidateProfileConversationHeader } from 'modules/candidate-profile/components/CandidateProfileConversationHeader';
import {
  CandidateProfileSearchInput,
  CandidateProfileSearchInputRef
} from 'modules/candidate-profile/forms/custom/CandidateProfileSearchInput';
import { splitMessageIntoSuggestionsAndText } from 'modules/candidate-profile/utils/splitMessageIntoSuggestionsAndText';
import {
  isSameSuggestion,
  SelectedSuggestion,
  toSuggestionTag
} from 'modules/candidate-profile/types/SelectedSuggestion';

type CandidateProfileSearchConversationProps = {
  conversationHistory: SearchCandidateVectorQueryMessage[];
  placeholderMessage: SearchCandidateVectorQueryMessage | null;
  candidateProfiles: CandidateProfileLinkData[];
  isQueryLoading: boolean;
  isReplying?: boolean;
  /** Filters the recruiter applied since the latest search. */
  manualFilters?: CandidateFilterState[];
  labelForField?: (field: string) => string;
  isSearchColumn?: (field: string) => boolean;
  describeRangeForChip?: (filter: CandidateRangeFilter) => string;
  /**
   * `removeFields` names the filters this message was sent to drop. The chips
   * do not change when the suggestion is selected or sent: the fields go with
   * the turn and leave when its reply is merged, so a turn that never lands
   * removes nothing.
   */
  onSearch: (searchQuery: string, removeFields?: string[]) => void;
  onRegenerateFromMessage: (agentMessageId: string) => string | null;
  onClose: () => void;
  isConversationInitialized: boolean;
  historyPreview?: React.ReactNode;
};

// A search is one request but three phases, and it takes long enough that a
// single "Searching" reads as a hang. These are timed rather than reported by
// the endpoint -- it answers once, at the end -- so the windows are approximate,
// and randomised within them so repeated searches do not look scripted. The
// last stage has no window: it holds until the request returns.
const SEARCH_STAGES: { label: string; minMs?: number; maxMs?: number }[] = [
  { label: 'Checking message for bias...', minMs: 4000, maxMs: 6000 },
  { label: 'Optimising search query and filters...', minMs: 6000, maxMs: 8000 },
  { label: 'Searching candidates...' }
];

const REPLY_LABEL = 'Summarising results...';

const getLoadingLabel = (
  isQueryLoading: boolean,
  isReplying: boolean,
  searchStage: number
): string => {
  if (isQueryLoading) return SEARCH_STAGES[searchStage].label;
  if (isReplying) return REPLY_LABEL;
  return '';
};

const LOADING_LABEL_FADE_MS = 300;

export const CandidateProfileSearchConversation: React.FC<
  CandidateProfileSearchConversationProps
> = ({
  conversationHistory,
  placeholderMessage,
  candidateProfiles,
  isQueryLoading = false,
  isReplying = false,
  manualFilters = [],
  labelForField = (field) => field,
  isSearchColumn,
  describeRangeForChip,
  onSearch,
  onRegenerateFromMessage,
  onClose,
  historyPreview,
  isConversationInitialized
}) => {
  const [searchParams] = useSearchParams();
  const hasPlaceholderMessage = !!placeholderMessage;
  const hasConversation =
    conversationHistory.length > 0 || hasPlaceholderMessage;
  const isEmptyState = isConversationInitialized && !hasConversation;
  const isLoading = isQueryLoading;
  const location = useLocation();

  const latestUserMessageRef = useRef<HTMLDivElement>(null);
  const anchoredUserMessageIdRef = useRef<string | null>(null);
  const searchInputRef = useRef<CandidateProfileSearchInputRef>(null);
  const mountedPathRef = useRef<string>(location.pathname);

  const [selectedSuggestions, setSelectedSuggestions] = useState<
    SelectedSuggestion[]
  >([]);

  const [searchStage, setSearchStage] = useState(0);

  useEffect(() => {
    if (!isQueryLoading) {
      setSearchStage(0);
      return;
    }

    // Each search re-rolls its own delays, so the timers live with the effect.
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    let elapsed = 0;

    SEARCH_STAGES.forEach((stage, index) => {
      if (stage.minMs === undefined || stage.maxMs === undefined) return;
      elapsed += stage.minMs + Math.random() * (stage.maxMs - stage.minMs);
      timeouts.push(setTimeout(() => setSearchStage(index + 1), elapsed));
    });

    return () => timeouts.forEach(clearTimeout);
  }, [isQueryLoading]);

  const loadingLabel = getLoadingLabel(isQueryLoading, isReplying, searchStage);

  const [displayedLoadingLabel, setDisplayedLoadingLabel] =
    useState(loadingLabel);
  const [isLoadingLabelVisible, setIsLoadingLabelVisible] = useState(true);

  useEffect(() => {
    if (!loadingLabel || loadingLabel === displayedLoadingLabel) return;
    setIsLoadingLabelVisible(false);
    const timeoutId = setTimeout(() => {
      setDisplayedLoadingLabel(loadingLabel);
      setIsLoadingLabelVisible(true);
    }, LOADING_LABEL_FADE_MS);
    return () => clearTimeout(timeoutId);
  }, [loadingLabel, displayedLoadingLabel]);

  const clearSelectedSuggestions = () => setSelectedSuggestions([]);

  const handleSearchSubmit = (searchQuery: string) => {
    const suggestionTags = selectedSuggestions.map(toSuggestionTag).join('');

    const finalMessage =
      suggestionTags && searchQuery
        ? `${suggestionTags} ${searchQuery}`
        : suggestionTags || searchQuery;

    const removeFields = selectedSuggestions.flatMap((suggestion) =>
      suggestion.field ? [suggestion.field] : []
    );

    onSearch(finalMessage, removeFields);
    clearSelectedSuggestions();
  };

  // Focus the search input after a chip so the Enter key can be used.
  const focusSearchInput = () =>
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

  const handleChipClick = (text: string, field?: string) => {
    const clicked: SelectedSuggestion = { text, ...(field ? { field } : {}) };

    setSelectedSuggestions((selected) => {
      const isSelected = selected.some((suggestion) =>
        isSameSuggestion(suggestion, clicked)
      );

      return isSelected
        ? selected.filter(
            (suggestion) => !isSameSuggestion(suggestion, clicked)
          )
        : [...selected, clicked];
    });
    focusSearchInput();
  };

  const handleRegenerateClick = (agentMessageId: string) => {
    const userMessageBody = onRegenerateFromMessage(agentMessageId);

    if (userMessageBody && searchInputRef.current) {
      // Restore the suggestions as chips - putting the raw body in the input
      // would show the <specific_suggestion> tags as text.
      const { suggestions, text } =
        splitMessageIntoSuggestionsAndText(userMessageBody);
      setSelectedSuggestions(suggestions);
      searchInputRef.current.setValue(text);
    }
  };

  const messages = [
    ...conversationHistory,
    ...(placeholderMessage ? [placeholderMessage] : [])
  ];

  const latestUserMessageIndex = messages.reduce(
    (latest, message, index) =>
      message.sender === SearchCandidateProfileMessageSender.User
        ? index
        : latest,
    -1
  );
  const latestUserMessageId = messages[latestUserMessageIndex]?.id ?? null;

  useEffect(() => {
    if (!latestUserMessageId || !latestUserMessageRef.current) return;
    if (latestUserMessageId === anchoredUserMessageIdRef.current) return;
    // Skip when the user navigated away mid-search, so the scroll does not
    // follow them onto another page.
    if (location.pathname !== mountedPathRef.current) return;

    anchoredUserMessageIdRef.current = latestUserMessageId;
    // Anchor the question to the top so it stays in sight while the reply
    // renders beneath it.
    latestUserMessageRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  }, [latestUserMessageId, location.pathname]);

  return (
    <Stack
      data-testid="candidate-search-conversation"
      sx={candidateProfileSearchConversationSx.searchContainer}
    >
      <Stack sx={candidateProfileSearchConversationSx.conversationHeader}>
        <CandidateProfileConversationHeader onClose={onClose} />
      </Stack>
      <Stack
        sx={candidateProfileSearchConversationSx.conversationRelativeContainer}
      >
        <Stack sx={candidateProfileSearchConversationSx.conversationContainer}>
          {isEmptyState && historyPreview}

          {/* Messages */}
          {messages.map((message, index) => (
            <Stack
              key={message.id}
              ref={
                index === latestUserMessageIndex ? latestUserMessageRef : null
              }
            >
              {message.sender === SearchCandidateProfileMessageSender.User ? (
                <CandidateProfileUserMessage
                  messageId={message.id}
                  messageBody={message.body}
                />
              ) : (
                <CandidateProfileAgentMessage
                  messageId={message.id}
                  messageBody={message.body}
                  candidateProfiles={candidateProfiles}
                  onRegenerate={() => handleRegenerateClick(message.id)}
                  isLoading={isLoading}
                  onChipClick={handleChipClick}
                  searchParams={Object.fromEntries(searchParams)}
                />
              )}
            </Stack>
          ))}
          {messages.length > 0 && !isQueryLoading && !isReplying && (
            <CandidateProfileFilterNotice
              filters={manualFilters}
              labelFor={labelForField}
              isSearchColumn={isSearchColumn}
              describeRangeForChip={describeRangeForChip}
            />
          )}
          {/* Show loading indicator at the bottom */}
          {((hasPlaceholderMessage && isQueryLoading) || isReplying) && (
            <Stack direction="row" alignItems="center" gap="20px">
              <AnalysingLoadingGif width={30} height={30} />
              <Typography
                variant="default"
                sx={candidateProfileSearchConversationSx.loadingLabel(
                  isLoadingLabelVisible,
                  LOADING_LABEL_FADE_MS
                )}
              >
                {displayedLoadingLabel}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <Stack
        sx={candidateProfileSearchConversationSx.conversationInputContainer}
      >
        <Stack gap="8px">
          <Typography
            sx={candidateProfileSearchConversationSx.conversationInputHeader}
          >
            {isEmptyState ? 'New search' : 'Write a message'}
          </Typography>
          {isEmptyState && (
            <Typography
              sx={
                candidateProfileSearchConversationSx.conversationInputSubheader
              }
            >
              Describe who you’re looking for. You can also add a boolean search
              string.
            </Typography>
          )}
        </Stack>
        <CandidateProfileSearchInput
          ref={searchInputRef}
          isLoading={isLoading}
          hasConversation={hasConversation}
          onSubmit={handleSearchSubmit}
          selectedSuggestions={selectedSuggestions}
          onChipClick={handleChipClick}
        />
      </Stack>
    </Stack>
  );
};
